import { type KeyboardEvent, type PointerEvent, type ReactNode, useId, useRef, useState } from 'react';
import { t } from '../../i18n';
import { type Period, uptimeBand } from './data';

// Gráficas del monitoreo, sin bibliotecas (como el Gantt de Proyectos). Una gráfica por medida, con un solo eje; línea
// fina en el color de marca, cuadrícula tenue, cursor con el valor al pasar el mouse o con las flechas del teclado y,
// para lectores de pantalla y para copiar, la misma información en una tabla.

export interface Point {
  at: string;
  value: number | null;
}

const timeLabel = (iso: string, period: Period) =>
  new Date(iso).toLocaleString('es-PA', period === '24h' ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', ...(period === '7d' ? { hour: '2-digit' } : {}) });

/** Máximo "redondo" del eje para valores sin tope (latencia). */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  return ([1, 2, 2.5, 5, 10].find((m) => m * pow >= v) ?? 10) * pow;
}

export function LineChart({
  title,
  points,
  period,
  format,
  max,
  threshold,
  current,
  note,
}: {
  title: string;
  points: Point[];
  period: Period;
  format: (v: number) => string;
  /** Tope fijo del eje (100 para porcentajes). Sin él, se calcula. */
  max?: number;
  /** Línea de referencia (el umbral de alerta del Panel). */
  threshold?: { value: number; label: string };
  /** Valor actual destacado junto al título. */
  current?: ReactNode;
  note?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const plot = useRef<HTMLDivElement>(null);
  const tableId = useId();
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const top = max ?? niceMax(Math.max(...values, threshold?.value ?? 0) * 1.15);
  const x = (i: number) => (points.length <= 1 ? 50 : (i / (points.length - 1)) * 100);
  const y = (v: number) => 100 - (Math.min(v, top) / top) * 100;

  // Un tramo por cada serie de puntos con datos: un hueco (sin revisiones) no se une con una línea inventada. Un punto
  // suelto (ej. la primera muestra) no forma línea: se dibuja como un punto.
  const segments: string[] = [];
  const isolated: number[] = [];
  let run: number[] = [];
  const close = () => {
    if (run.length === 1) isolated.push(run[0]!);
    else if (run.length > 1) segments.push(run.map((i, k) => `${k ? 'L' : 'M'}${x(i) * 10} ${y(points[i]!.value!)}`).join(' '));
    run = [];
  };
  points.forEach((p, i) => (p.value === null ? close() : run.push(i)));
  close();

  const pick = (e: PointerEvent<HTMLDivElement>) => {
    const r = plot.current!.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => Math.max(0, Math.min(points.length - 1, (h ?? points.length - 1) + (e.key === 'ArrowLeft' ? -1 : 1))));
  };
  const h = hover !== null ? points[hover] : undefined;

  return (
    <figure className="space-y-2">
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">{title}</span>
        {current !== undefined && <span className="text-sm font-semibold text-slate-900">{current}</span>}
      </figcaption>
      {values.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">{t('mon.sin_puntos')}</p>
      ) : (
        <>
          <div className="flex gap-2">
            <div className="flex h-36 w-12 shrink-0 flex-col justify-between text-right text-[11px] tabular-nums text-slate-500" aria-hidden>
              <span>{format(top)}</span>
              <span>{format(top / 2)}</span>
              <span>{format(0)}</span>
            </div>
            <div
              ref={plot}
              className="relative h-36 flex-1 cursor-crosshair rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40"
              tabIndex={0}
              role="group"
              aria-label={`${title}. ${t('mon.tabla_datos')}`}
              aria-describedby={tableId}
              onPointerMove={pick}
              onPointerLeave={() => setHover(null)}
              onKeyDown={keys}
              onBlur={() => setHover(null)}
            >
              <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
                {[0, 50, 100].map((g) => (
                  <line key={g} x1="0" x2="1000" y1={g} y2={g} className="stroke-slate-200" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
                {threshold && threshold.value <= top && (
                  <line x1="0" x2="1000" y1={y(threshold.value)} y2={y(threshold.value)} className="stroke-senal" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                )}
                {segments.map((d, i) => (
                  <path key={i} d={d} fill="none" className="stroke-marca" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
              {isolated.map((i) => (
                <div
                  key={i}
                  className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-marca"
                  style={{ left: `${x(i)}%`, top: `${y(points[i]!.value!)}%` }}
                />
              ))}
              {threshold && threshold.value <= top && (
                <span className="pointer-events-none absolute right-1 -translate-y-full text-[11px] text-amber-900" style={{ top: `${y(threshold.value)}%` }}>
                  {threshold.label}
                </span>
              )}
              {h && hover !== null && (
                <>
                  <div className="pointer-events-none absolute inset-y-0 w-px bg-slate-400" style={{ left: `${x(hover)}%` }} />
                  {h.value !== null && (
                    <div
                      className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-marca"
                      style={{ left: `${x(hover)}%`, top: `${y(h.value)}%` }}
                    />
                  )}
                  <div
                    role="status"
                    className={`pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1 text-xs shadow-sm ${x(hover) > 70 ? '-translate-x-full -ml-2' : 'ml-2'}`}
                    style={{ left: `${x(hover)}%` }}
                  >
                    <span className="block text-slate-500">{timeLabel(h.at, period)}</span>
                    <span className="font-semibold text-slate-900">{h.value === null ? t('mon.sin_datos') : format(h.value)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="ml-14 flex justify-between text-[11px] text-slate-500" aria-hidden>
            <span>{timeLabel(points[0]!.at, period)}</span>
            {points.length > 2 && <span>{timeLabel(points[Math.floor(points.length / 2)]!.at, period)}</span>}
            {points.length > 1 && <span>{timeLabel(points.at(-1)!.at, period)}</span>}
          </div>
          {note && <p className="text-xs text-slate-500">{note}</p>}
          <details className="text-xs text-slate-600" id={tableId}>
            <summary className="cursor-pointer select-none">{t('mon.tabla_datos')}</summary>
            <div className="mt-2 max-h-56 overflow-y-auto">
              <table className="w-full text-left">
                <thead className="text-slate-500">
                  <tr>
                    <th className="py-1 pr-4 font-medium">{t('mon.col_momento')}</th>
                    <th className="py-1 font-medium">{t('mon.col_valor')}</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {points.map((p) => (
                    <tr key={p.at}>
                      <td className="py-0.5 pr-4">{timeLabel(p.at, period)}</td>
                      <td className="py-0.5">{p.value === null ? t('mon.sin_datos') : format(p.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </figure>
  );
}

const BAND = {
  bien: { cls: 'bg-green-600', label: 'mon.leyenda_bien' },
  regular: { cls: 'bg-senal', label: 'mon.leyenda_regular' },
  mal: { cls: 'bg-red-600', label: 'mon.leyenda_mal' },
  sin: { cls: 'bg-slate-200', label: 'mon.leyenda_sin' },
} as const;

/** Disponibilidad de cada uno de los últimos 30 días: una barra por día, con leyenda y el valor en el tooltip. */
export function UptimeStrip({ days }: { days: Array<{ day: string; uptime: number | null }> }) {
  const byDay = new Map(days.map((d) => [d.day, d.uptime]));
  const today = new Date();
  const all = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29 + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { key, label: d.toLocaleDateString('es-PA', { day: 'numeric', month: 'short' }), uptime: byDay.get(key) ?? null };
  });
  const value = (v: number | null) => (v === null ? t('mon.leyenda_sin') : `${v.toLocaleString('es-PA', { maximumFractionDigits: 2 })} %`);
  return (
    <figure className="space-y-2">
      <figcaption className="text-sm font-medium text-slate-800">{t('mon.uptime_dias')}</figcaption>
      <ol className="flex h-10 items-stretch gap-0.5">
        {all.map((d) => (
          <li
            key={d.key}
            className={`flex-1 rounded-sm ${BAND[uptimeBand(d.uptime)].cls}`}
            title={t('mon.dia_tooltip', { dia: d.label, valor: value(d.uptime) })}
          >
            <span className="sr-only">{t('mon.dia_tooltip', { dia: d.label, valor: value(d.uptime) })}</span>
          </li>
        ))}
      </ol>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {(Object.keys(BAND) as Array<keyof typeof BAND>).map((k) => (
          <li key={k} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-sm ${BAND[k].cls}`} aria-hidden />
            {t(BAND[k].label)}
          </li>
        ))}
      </ul>
    </figure>
  );
}

/** Cifra destacada con una barra de nivel (porcentajes del servidor). Pasado el umbral, la barra va en ámbar. */
export function Meter({ label, value, threshold, sub }: { label: string; value: number | null; threshold?: number; sub?: ReactNode }) {
  const over = value !== null && threshold !== undefined && value >= threshold;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium text-slate-600">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value === null ? t('mon.sin_datos') : `${Math.round(value)} %`}</p>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value ?? undefined}
      >
        <div className={`h-full rounded-full ${over ? 'bg-senal' : 'bg-marca'}`} style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
      {(sub || over) && (
        <p className={`mt-1.5 text-xs ${over ? 'font-medium text-amber-900' : 'text-slate-500'}`}>
          {over && threshold !== undefined ? `${t('mon.umbral', { n: threshold })}${sub ? ' · ' : ''}` : ''}
          {sub}
        </p>
      )}
    </div>
  );
}

/** Cifra simple (disponibilidad, latencia). */
export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium text-slate-600">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

/** Selector del período de las gráficas: una sola fila arriba de todas las gráficas que afecta. */
export function PeriodPicker({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return (
    <div role="radiogroup" aria-label={t('mon.periodo')} className="inline-flex rounded border border-slate-300 bg-white p-0.5">
      {(['24h', '7d', '30d'] as Period[]).map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={value === p}
          onClick={() => onChange(p)}
          className={`rounded-sm px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40 ${value === p ? 'bg-marca text-white' : 'text-slate-700 hover:bg-slate-100'}`}
        >
          {t(`mon.periodo_${p}`)}
        </button>
      ))}
    </div>
  );
}
